import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import MicroLabel from "@/components/base/MicroLabel";

interface RiskTool {
  title: string;
  builtOn: string;
  text: string;
  use: string;
  to: string;
  linkLabel: string;
}

const RISK_TOOLS: RiskTool[] = [
  {
    title: "Stop-loss",
    builtOn: "Stop order",
    text: "Sits at the price that proves the trade wrong. When price reaches it, the position is closed at market — the exit happens whether or not you are watching, though a fast move can fill you past your level.",
    use: "Every leveraged position. Place it when you enter, not after the trade turns.",
    to: "/stop-orders",
    linkLabel: "How a stop fills",
  },
  {
    title: "Take-profit",
    builtOn: "Limit order",
    text: "A limit order attached to an open position. It closes the trade the moment your target trades, and because it is a limit it can never fill worse than your level.",
    use: "Any trade with a clear level where you want out — the winner is banked without you.",
    to: "/limit-orders",
    linkLabel: "How a limit fills",
  },
  {
    title: "Trailing stop",
    builtOn: "Stop order",
    text: "A stop that follows price as it moves in your favour, staying a fixed distance behind it. It ratchets forward and never back, so a grown profit cannot quietly become a loss.",
    use: "Trend trades you plan to ride without naming a fixed target.",
    to: "/stop-orders",
    linkLabel: "How a stop fills",
  },
  {
    title: "OCO order",
    builtOn: "Stop + limit",
    text: "Two linked orders held as a pair — one a stop, one a limit, the same two prices a stop-limit is built from. The moment either one fills, the other is cancelled, so a single idea cannot fill you twice.",
    use: "Breakout setups where either direction could trigger first.",
    to: "/stop-limit-orders",
    linkLabel: "How stop-limits work",
  },
];

export default function RiskTools() {
  return (
    <section className="px-4 md:px-8 py-16 md:py-24 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          eyebrow="Risk tools"
          title="Execution is not just about entry"
          description="The orders attached to a trade decide how it ends. These four do most of the work."
        />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {RISK_TOOLS.map((tool, index) => (
            <Reveal key={tool.title} delay={index * 90} className="h-full">
              <Link
                to={tool.to}
                className="h-full flex flex-col border-2 border-foreground-950 bg-background-50 p-6 transition-colors hover:bg-foreground-950 group cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3 border-b-2 border-foreground-950 pb-4 group-hover:border-background-50">
                  <MicroLabel mark="dot" tone="accent" className="group-hover:text-accent-300">
                    Built on {tool.builtOn}
                  </MicroLabel>
                  <MicroLabel tone="muted" className="group-hover:text-background-400">
                    0{index + 1}
                  </MicroLabel>
                </div>

                <h3 className="mt-5 font-heading font-bold uppercase tracking-tight text-lg text-foreground-950 group-hover:text-background-50">
                  {tool.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600 group-hover:text-background-300">
                  {tool.text}
                </p>

                <div className="mt-auto pt-5">
                  <div className="border-t-2 border-foreground-950 pt-4 group-hover:border-background-50">
                    <MicroLabel tone="muted" className="group-hover:text-background-400">
                      Reach for it when
                    </MicroLabel>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-700 group-hover:text-background-300">
                      {tool.use}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 font-label text-[10px] uppercase tracking-[0.18em] font-semibold text-foreground-950 group-hover:text-accent-300 whitespace-nowrap">
                      {tool.linkLabel}
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}